import React, { useEffect } from 'react';
import { Box } from '@mui/material';

// Inject animations into document head
const injectAnimations = () => {
  if (document.getElementById('tms-loader-styles')) return;

  const style = document.createElement('style');
  style.id = 'tms-loader-styles';
  style.textContent = `
    @keyframes tmsLoaderDash {
      0%, 100% { stroke-dashoffset: 282.7; }
      50% { stroke-dashoffset: 0; }
    }
    @keyframes tmsLoaderSpin {
      to { transform: rotate(360deg); }
    }
    @keyframes tmsLoaderPulse {
      0%, 100% { opacity: 1; r: 55; }
      50% { opacity: 0.3; r: 75; }
    }
    .tms-loader-dash {
      animation: tmsLoaderDash 2s ease-in-out infinite !important;
      stroke-linecap: round;
    }
    .tms-loader-spin {
      animation: tmsLoaderSpin 1.5s linear infinite !important;
    }
    .tms-loader-pulse {
      animation: tmsLoaderPulse 1.5s ease-in-out infinite !important;
    }
  `;
  document.head.appendChild(style);
};

export const CircularTMSLoader = ({
  size = 120,
  variant = 'progress',
  percentage = null,
  showLabel = false,
  label = 'Loading...',
  color = '#3361FF',
  accentColor = '#7c3aed'
}) => {
  useEffect(() => {
    injectAnimations();
  }, []);
  const baseLogoSize = size * 0.95;
  const ringRadius = size * 0.46;

  const variants = {
    progress: (
      <svg
        width={size}
        height={size}
        viewBox='0 11 120 120'
        style={{ display: 'block' }}
      >
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke='#e2e8f0'
          strokeWidth='2'
        />
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke={color}
          strokeWidth='2'
          strokeDasharray={ringRadius * 3.14159}
          strokeDashoffset={ringRadius * 3.14159}
          className='tms-loader-dash'
        />
      </svg>
    ),
    rotating: (
      <svg
        width={size}
        height={size}
        viewBox='0 11 120 120'
        style={{ display: 'block' }}
        className='tms-loader-spin'
      >
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke='#e2e8f0'
          strokeWidth='2'
        />
        <path
          d={`M 60 ${60 - ringRadius} A ${ringRadius} ${ringRadius} 0 0 1 ${60 + ringRadius} 60`}
          fill='none'
          stroke={color}
          strokeWidth='2'
          strokeLinecap='round'
        />
      </svg>
    ),
    double: (
      <svg width={size} height={size} viewBox='0 0 120 120'>
        <circle
          cx='60'
          cy='60'
          r={ringRadius - 8}
          fill='none'
          stroke='#e2e8f0'
          strokeWidth='1.5'
        />
        <circle
          cx='60'
          cy='60'
          r={ringRadius - 8}
          fill='none'
          stroke={color}
          strokeWidth='1.5'
          strokeDasharray={ringRadius * 3.14159}
          strokeDashoffset={ringRadius * 3.14159}
          className='tms-loader-dash'
        />
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke='#e2e8f0'
          strokeWidth='1.5'
        />
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke={accentColor}
          strokeWidth='1.5'
          strokeDasharray={ringRadius * 3.14159}
          strokeDashoffset={ringRadius * 3.14159}
          className='tms-loader-dash'
          style={{ animationDelay: '0.2s' }}
        />
      </svg>
    ),
    pulse: (
      <svg width={size} height={size} viewBox='0 0 120 120'>
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke='#e2e8f0'
          strokeWidth='2'
        />
        <circle
          cx='60'
          cy='60'
          r={ringRadius}
          fill='none'
          stroke={color}
          strokeWidth='2'
          className='tms-loader-pulse'
        />
      </svg>
    )
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Progress Ring */}
        {variants[variant] || variants.progress}

        {/* Center Logo - No Box wrapper, just SVG positioned absolutely */}
        <svg
          width={size * 1.08}
          height={size * 1.08}
          viewBox='0 0 262 137'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{
            display: 'block',
            position: 'absolute',
            left: '50%',
            top: '50%',
            marginLeft: -(size * 1.08) / 2,
            marginTop: -(size * 1.08) / 2
          }}
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M114.787 1.52588e-05L108.423 6.36398L123.308 21.2488C127.603 25.5445 134.568 25.5445 138.864 21.2488L153.749 6.36398L147.385 1.94595e-05L132.5 14.8848C131.719 15.6658 130.453 15.6658 129.672 14.8848L114.787 1.52588e-05Z'
            fill={color}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M167.172 19.7867L160.808 13.4227L145.923 28.3076C141.627 32.6033 141.627 39.5681 145.923 43.8639L160.808 58.7489L167.172 52.385L152.287 37.4999C151.506 36.7189 151.506 35.4526 152.287 34.6715L167.172 19.7867Z'
            fill={color}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M114.787 72.1716L108.423 65.8077L123.308 50.9229C127.603 46.6271 134.568 46.6271 138.864 50.9229L153.749 65.8077L147.385 72.1716L132.5 57.2869C131.719 56.5058 130.453 56.5058 129.672 57.2869L114.787 72.1716Z'
            fill={color}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M95.0002 19.7867L101.364 13.4227L116.249 28.3076C120.545 32.6033 120.545 39.5681 116.249 43.8639L101.364 58.749L95 52.385L109.885 37.4999C110.666 36.7189 110.666 35.4526 109.885 34.6715L95.0002 19.7867Z'
            fill={color}
          />
        </svg>

        {/* Percentage if provided */}
        {percentage && (
          <Box
            sx={{
              position: 'absolute',
              fontSize: size * 0.12,
              fontWeight: 600,
              color,
              bottom: size * 0.1
            }}
          >
            {percentage}%
          </Box>
        )}
      </Box>

      {/* Label */}
      {showLabel && (
        <Box sx={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>
          {label}
        </Box>
      )}
    </Box>
  );
};

// Export for easy usage
export default CircularTMSLoader;
